import './viewManager.less';

import React from 'react';

import { Designer } from 'controls/designer/designer';
import { Action } from 'controls/designer/actionsManager/action';
import { Actions } from 'controls/designer/actionsManager/actions';
import { AntFlex } from 'controls/react/ant/antFlex';
import { AntButton } from 'controls/react/ant/antButton';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import {
	predefinedResolutions,
	ResolutionSelector,
	ResolutionSelectorStore,
} from 'controls/designer/features/resolutionSelector';
import { MxCell } from 'controls/designer/mxGraphInterfaces';
import { DesignerStore } from 'controls/designer/designerStore';
import { isMobileBrowser } from 'tools/utils';
import { makeAutoObservable } from 'mobx';

const i18n = require('core/localization/localization').translator({
	'Main dashboard': { no: 'Hoveddashboard' },
	'Mobile dashboard': { no: 'Mobilt dashboard' },
	'Enable mobile dashboard': {},
	'Disable mobile dashboard': {},
	'In order to enable mobile dashboard you need to save it first.': {},
	'Mobile dashboard link tooltip': {
		en: 'Click to generate a mobile-friendly version of the Main Dashboard.',
		no: 'Det mobile dashboardet gjenspeiler widgetene og datakildene fra hoveddashboardet.',
	},
	'Mobile dashboard help': {
		en: 'The mobile dashboard replicates the widgets and data sources from the Main Dashboard. When new widgets are added to the Main Dashboard, they will also appear in the mobile version for placement. Data source changes will automatically sync between the two dashboards. However, any widget changes (such as adding or deleting) made in the mobile dashboard will not impact the Main Dashboard.\n\n The mobile dashboard is automatically displayed when a mobile device is detected while accessing the public dashboard link for the Main Dashboard.',
		no: 'Når nye widgeter legges til i hoveddashboardet, vil de også vises i mobilversjonen for plassering. Endringer i datakilder synkroniseres automatisk mellom de to dashboardene. Endringer i widgeter (for eksempel legge til eller slette) som gjøres i det mobile dashboardet, vil imidlertid ikke påvirke hoveddashboardet.\n\n Det mobile dashboardet vises automatisk når en mobil enhet blir oppdaget ved tilgang til den offentlige dashboard-lenken for hoveddashboardet.',
	},
	'Mobile dashboard tips': {},
	'View selector': {},
});

export const notInViewColor = '#da1e27';

const b = require('b_').with('designer-alternative-views-selector');

export class ViewManager {
	view: string;

	constructor(public store: DesignerStore) {
		makeAutoObservable(this);
	}

	get data() {
		return this.store.data;
	}

	get settings() {
		return this.data.viewSettings.viewMap[this.view];
	}

	init() {
		this.addActions();
		//this.updateMxGraphCode()
	}

	initializeDataStructure() {
		if (this.data.viewSettings == null) {
			this.data.viewSettings = {
				viewMap: {},
			};
		}

		if (this.data.viewSettings.viewMap == null) {
			this.data.viewSettings.viewMap = {};
		}

		if (this.store.config.view) {
			this.view = this.store.config.view;
		} else {
			if (this.store.config.chromeless && isMobileBrowser() && this.data.viewSettings.viewMap['mobile']?.enabled) {
				this.view = 'mobile';
			} else {
				this.view = 'basic';
			}
		}

		if (this.data.viewSettings.viewMap[this.view] == null) {
			this.data.viewSettings.viewMap[this.view] = {
				deletedIds: [],
				cellsData: {},
				resolution: { ...predefinedResolutions[0].resolution },
			};
		}

		this.settings.enabled = true;

		if (this.settings.cellsData == null) {
			this.settings.cellsData = {};
		}
	}

	addActions() {
		const store = this.store;

		if (!this.settings.helpTextShown && !store.config.chromeless && this.view == 'mobile') {
			this.settings.helpTextShown = true;
			store.showHelpText({
				message: i18n('Mobile dashboard tips'),
				description: (
					<div dangerouslySetInnerHTML={{ __html: i18n('Mobile dashboard help').replace('\n', '<br/>') }}></div>
				),
			});
		}

		const mobileEnabled = store.data.viewSettings.viewMap['mobile']?.enabled == true && store.config.persisted;
		const mobileViewTitle = !store.config.persisted
			? i18n('In order to enable mobile dashboard you need to save it first.')
			: (!mobileEnabled ? i18n('Mobile dashboard link tooltip') + '\n\n' : '') + i18n('Mobile dashboard help');

		store.am.add(
			new Action({
				id: Actions.SelectView,
				title: i18n('View selector'),
				toolbarEntryRenderer: () => {
					return (
						<AntFlex align={'center'} gap={'large'}>
							<div
								className={b('view-link', { current: this.view == 'basic' })}
								onClick={() => store.switchToView('basic')}
							>
								{i18n('Main dashboard')}
							</div>
							<div
								className={b('view-link', { disabled: !mobileEnabled, current: this.view == 'mobile' })}
								title={mobileViewTitle}
								onClick={store.config.persisted ? () => store.switchToView('mobile') : null}
							>
								{mobileEnabled ? i18n('Mobile dashboard') : i18n('Enable mobile dashboard')}
							</div>
							{this.view == 'mobile' && (
								<div title={i18n('Disable mobile dashboard')}>
									<AntButton
										onClick={() => store.viewManager.disableView()}
										type={'text'}
										icon={<EyeInvisibleOutlined />}
									/>
								</div>
							)}
						</AntFlex>
					);
				},
			}),
		);

		let pageSizeStore: ResolutionSelectorStore;
		store.am.add(
			new Action({
				id: Actions.SelectPageSize,
				title: 'Update Resolution',
				visible: store.config.view == 'mobile' && store.config.chromeless != true,
				toolbarEntryRenderer: () => {
					if (pageSizeStore == null) {
						pageSizeStore = new ResolutionSelectorStore({
							width: this.settings.resolution.width,
							height: this.settings.resolution.height,
							store,
							onChange: (width, height) => {
								store.ui.setPageFormat({
									width: width,
									height: height,
								});

								this.settings.resolution = {
									width: width,
									height: height,
								};

								store.graph.view.revalidate();
							},
						});
					}
					return <ResolutionSelector key={Actions.SelectPageSize} store={pageSizeStore} />;
				},
			}),
		);

		const originalDestroy = store.legacyDesigner.destroy;
		store.legacyDesigner.destroy = function () {
			pageSizeStore?.destroy();
			// @ts-ignore
			originalDestroy.apply(this, arguments);
		};
	}

	markAsDeleted = (cell: MxCell) => {
		this.settings.deletedIds.push(cell.getUniqueId());
	};

	removeDeletedMark = (cell: MxCell) => {
		const index = this.settings.deletedIds.indexOf(cell.getUniqueId());
		if (index != -1) {
			this.settings.deletedIds.splice(index, 1);
		}
	};

	getCellsData(cell: MxCell, createIfNotExists: boolean = false) {
		let result = this.settings.cellsData[cell.getUniqueId()];

		if (result == null && createIfNotExists) {
			this.settings.cellsData[cell.getUniqueId()] = {};
			result = this.settings.cellsData[cell.getUniqueId()];
		}

		return result;
	}

	getAnchoringData(cell: MxCell) {
		const cellsData = this.getCellsData(cell, true);
		if (cellsData.anchors == null) {
			cellsData.anchors = {};
		}

		const anchors = cellsData.anchors;

		if (anchors.left === null || anchors.left === undefined) {
			anchors.left = true;
		}

		if (anchors.right === null || anchors.right === undefined) {
			anchors.right = false;
		}

		return anchors;
	}

	disableView = () => {
		this.settings.enabled = false;
		this.settings.helpTextShown = false;
		this.store.switchToView('basic');
	};
}
